import { useAuth0 } from '@auth0/auth0-react';
import React, { createContext, memo, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useMatch } from 'react-router-dom';
import { isNullOrUndefined } from '@truescope-web/utils/lib/objects';
import { webSocketEndpoint } from '@truescope-web/utils/lib/websockets';
import { handleChunkWithGrouping } from './constants';

const WebSocketContext = createContext(null);

export const useWebSocketContext = () => {
	const context = useContext(WebSocketContext);
	if (isNullOrUndefined(context)) {
		throw new Error('WebSocketContext must be used within a WebSocketProvider');
	}
	return context;
};

const isSocketOpen = (socket) => socket?.readyState === WebSocket.CONNECTING || socket?.readyState === WebSocket.OPEN;

const WebSocketProvider = ({ children }) => {
	const { isLoading, isAuthenticated, getAccessTokenSilently } = useAuth0();
	const [isReady, setIsReady] = useState(false);
	const webSocket = useRef();
	const connectRef = useRef();
	const match = useMatch('/w/:workspaceId/*') || {};
	const routeWorkspaceId = parseInt(match?.params?.workspaceId, 10);
	const previousWorkspace = useRef();
	const receivedChunksByGroupRef = useRef({});

	const handleWebsocketEvent = useCallback((event) => {
		const data = JSON.parse(event.data);
		const message = handleChunkWithGrouping(data, receivedChunksByGroupRef);

		if (isNullOrUndefined(message)) {
			// waiting for more chunks to process
			return;
		}

		const customEvent = new CustomEvent('onmessage', {
			detail: message
		});

		webSocket.current.dispatchEvent(customEvent);
	}, []);

	const connect = useCallback(
		(force = false) =>
			new Promise(async (resolve) => {
				if (
					isNaN(routeWorkspaceId) ||
					(!force &&
						(isLoading ||
							!isAuthenticated ||
							previousWorkspace.current === routeWorkspaceId ||
							isSocketOpen(webSocket.current)))
				) {
					return;
				}

				previousWorkspace.current = routeWorkspaceId;

				const token = await getAccessTokenSilently();
				const socket = new WebSocket(`${webSocketEndpoint}?token=Bearer ${token}&workspaceId=${routeWorkspaceId}`);
				webSocket.current = socket;

				socket.onopen = () => {
					webSocket.current.addEventListener('message', handleWebsocketEvent);
					setIsReady(true);
					resolve(socket);
				};
				socket.onclose = async (event) => {
					webSocket.current.removeEventListener('message', handleWebsocketEvent);
					setIsReady(false);
					if (event.reason === 'forced') {
						return;
					}
					while (webSocket.current.readyState !== WebSocket.CLOSED) {
						await new Promise((resolve) => setTimeout(resolve, 1000));
					}
					await connect(true);
				};
				socket.onerror = async (e) => {
					console.error('WebSocket error', e);
				};
			}),
		[isLoading, isAuthenticated, getAccessTokenSilently, setIsReady, routeWorkspaceId, previousWorkspace]
	);

	connectRef.current = connect;

	useEffect(() => {
		if (webSocket.current?.readyState && isSocketOpen(webSocket.current)) {
			return;
		}

		const initiate = async () => {
			await connect();
		};

		initiate();

		return () => {
			const socket = webSocket.current;
			if (isNullOrUndefined(socket)) {
				return;
			}

			previousWorkspace.current = undefined;
			if (!isNullOrUndefined(socket?.close) && isSocketOpen(socket)) {
				socket?.close(1000, 'forced');
			}
		};
	}, [connect]);

	const send = useCallback(async (message) => {
		try {
			if (!isSocketOpen(webSocket.current)) {
				await connectRef.current(true);
			}
			webSocket.current.send(message);
		} catch (e) {
			console.error('Websocket send error', e);
			throw e;
		}
	}, []);

	return <WebSocketContext.Provider value={[isReady, webSocket, send]}>{children}</WebSocketContext.Provider>;
};

export default memo(WebSocketProvider);
